import Decimal from "decimal.js-light";

import {
  EXTERNAL_NOTE_TYPE,
  PRODUCT_TYPE_FLASHING_LABEL,
  PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
  PRODUCT_TYPE_NOTES_ITEM_LABEL,
} from "@/utils/constants";

export const DISPLAY_MODE_FULL_BREAKDOWN = "full_breakdown";
export const DISPLAY_MODE_GROUPED = "grouped";
export const DISPLAY_MODE_ONE_LINER = "one_liner";

export const GROUPED_LABEL_MATERIALS = "Materials";
export const GROUPED_LABEL_LABOUR = "Labour";

export const INVOICE_LIKE_VIEW_TYPES = ["invoice", "proforma_invoice", "partial_invoice"];

export const isSummaryInvoiceMode = ({ isNewSummaryInvoicing, type, displayMode } = {}) =>
  Boolean(isNewSummaryInvoicing) &&
  INVOICE_LIKE_VIEW_TYPES.includes(type) &&
  [DISPLAY_MODE_GROUPED, DISPLAY_MODE_ONE_LINER].includes(displayMode);

const safeTotal = (value) => {
  if (value == null || value === "") {
    return Decimal(0);
  }
  const numeric = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(numeric)) {
    return Decimal(0);
  }
  return Decimal(numeric);
};

const isIncludedKitChild = (child) =>
  child.productType !== PRODUCT_TYPE_NOTES_ITEM_LABEL || child.notesType === EXTERNAL_NOTE_TYPE;

export const computeGroupedTotals = ({ products, drawings } = {}) => {
  let materials = Decimal(0);
  let labour = Decimal(0);

  const classifyLeaf = (item) => {
    if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
      labour = labour.plus(safeTotal(item.totalValue));
    } else {
      materials = materials.plus(safeTotal(item.totalValue));
    }
  };

  (products || [])
    .filter((item) => !item.isDeleted)
    .forEach((item) => {
      // Notes carry no money. Flashings are skipped here because their priced total is folded
      // into Materials from the separate `drawings` array below (guards against double-count).
      if (
        item.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL ||
        item.productType === PRODUCT_TYPE_FLASHING_LABEL
      ) {
        return;
      }

      if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
        labour = labour.plus(safeTotal(item.totalValue));
        return;
      }

      if (item.customPricing) {
        materials = materials.plus(safeTotal(item.totalValue));
        return;
      }

      const kitItems = (item.productKitItems || []).filter(isIncludedKitChild);
      const subKitProducts = (item.subKits || []).flatMap((subKit) =>
        (subKit.kitProducts || []).filter(isIncludedKitChild)
      );

      if (kitItems.length === 0 && subKitProducts.length === 0) {
        materials = materials.plus(safeTotal(item.totalValue));
        return;
      }

      kitItems.forEach(classifyLeaf);
      subKitProducts.forEach(classifyLeaf);
    });

  (drawings || []).forEach((d) => {
    materials = materials.plus(safeTotal(d.price?.customPrice || d.price?.totalPrice));
  });

  return { materials: materials.toFixed(2), labour: labour.toFixed(2) };
};

export const resolveGroupedLabels = ({ labourLabel, materialsLabel } = {}) => ({
  labour: (labourLabel ?? "").trim() || GROUPED_LABEL_LABOUR,
  materials: (materialsLabel ?? "").trim() || GROUPED_LABEL_MATERIALS,
});

export const defaultOneLinerName = ({ order = {}, activeCustomer } = {}) => {
  const orderPart = order.purchaseOrder ? `Order #${order.purchaseOrder}` : "";
  const customerName = activeCustomer?.companyName || order.customerName || "";
  if (orderPart && customerName) {
    return `${orderPart} - ${customerName}`;
  }
  return orderPart || customerName;
};
